/**
 * Which sprint a screen belongs to, and whether this build shows it.
 *
 * <p>The product is finished; this is about *pacing the reveal* — and, since
 * the Consulting Agreement ties invoicing to "end of each 2-week sprint",
 * about what the client can actually validate before an invoice goes out.
 * Sprint 1 = M1 (Account/Profile/Settings) + M2 (Media & Geo), Sprint 2 = M3 +
 * M4, Sprint 3 = M5 + M6 + M7, Sprint 4 = M8 + M9 + M10 — the same table as
 * `implementation/ROADMAP-40-DAYS.md` and Article 3 of the signed agreement.
 *
 * <p>Every one of the 115 refs in `WaaGo_Matrice_Fonctionnalites.xlsx` is
 * assigned to one of these ten milestones in
 * `WaaGo_Matrice_Fonctionnalites_Sprints.xlsx` — that workbook, not this
 * file's comments, is the source of truth for *why* a given ref lands where
 * it does; its "Commentaire" column carries the reasoning for every ref the
 * contract doesn't name outright.
 *
 * <p>Nothing is deleted and no branch is cut. Moving to the next sprint is
 * `VITE_SPRINT=2` and a rebuild — the code for all four is in every bundle,
 * which is the point: there is no merge to get wrong on the morning of a
 * demonstration.
 *
 * <p>This is a **presentation** control, not a security one. The routes and
 * the API are all still there, and someone who edits a URL will reach them.
 * That is the correct trade for a staged client demo and would be the wrong
 * one for anything else.
 *
 * ## The mapping
 *
 * | Sprint | Days  | Milestones                                             |
 * |--------|-------|---------------------------------------------------------|
 * | 1      | 1–10  | M1 Account/Profile/Settings · M2 Media & Geo Platform   |
 * | 2      | 11–20 | M3 AF Onboarding & KYC · M4 AF Operations               |
 * | 3      | 21–30 | M5 Tracking & Messaging · M6 Money · M7 Back-Office     |
 * | 4      | 31–40 | M8 Notifications & Support · M9 Offline/i18n · M10 Go-Live |
 *
 * Each app declares its own path→sprint table and calls
 * {@link registerSprintRoutes} once at boot. They are separate tables because
 * `/compte` means something different in each app.
 */

export type Sprint = 1 | 2 | 3 | 4

/** What each sprint covers, for the text shown on a locked screen. */
export const SPRINT_PLAN: Record<Sprint, { days: string; title: string; milestones: string }> = {
  1: { days: 'Jours 1–10', title: 'Compte, profil et plateforme média/géolocalisation', milestones: 'M1 · M2' },
  2: { days: 'Jours 11–20', title: 'Espace prestataire — inscription et opérations', milestones: 'M3 · M4' },
  3: { days: 'Jours 21–30', title: 'Suivi en temps réel, paiements et back-office', milestones: 'M5 · M6 · M7' },
  4: { days: 'Jours 31–40', title: 'Notifications, hors-ligne, accessibilité et mise en production', milestones: 'M8 · M9 · M10' },
}

/**
 * The sprint this build shows. Everything above it is locked.
 *
 * <p>Defaults to 4 — the whole product — so a developer running `pnpm dev`, and
 * any build that forgets the variable, sees everything. A staged demo is the
 * exception and has to ask for it; a build that silently hid three quarters of
 * the product because a variable was unset would be a bad default.
 */
export const CURRENT_SPRINT: Sprint = parseSprint(import.meta.env.VITE_SPRINT)

/**
 * What a locked entry looks like.
 *
 * - `badge` — it stays visible, greyed, with a padlock and the sprint it
 *   arrives in. The roadmap is part of the story and each reveal reads as
 *   progress.
 * - `hidden` — it is not there at all, and its route 404s. The app looks
 *   smaller but finished, and there is nothing to click and explain away.
 */
export const LOCK_STYLE: 'badge' | 'hidden' =
  import.meta.env.VITE_LOCK_STYLE === 'hidden' ? 'hidden' : 'badge'

function parseSprint(raw: unknown): Sprint {
  const n = Number(raw)
  return n === 1 || n === 2 || n === 3 || n === 4 ? n : 4
}

/** A path pattern and the sprint that delivers it. `:param` matches one segment. */
export type SprintRoute = { path: string; sprint: Sprint }

let ROUTES: SprintRoute[] = []

/**
 * Install this app's table. Call once, before the router renders.
 *
 * <p>Order does not matter: {@link sprintOf} picks the most specific match, so
 * `/compte/paiement` wins over `/compte` however the table is written.
 */
export function registerSprintRoutes(routes: SprintRoute[]): void {
  ROUTES = routes
}

/** How many literal segments a pattern has — its specificity. */
function specificity(pattern: string): number {
  return pattern.split('/').filter((s) => s && !s.startsWith(':')).length
}

function matches(pattern: string, pathname: string): boolean {
  const p = pattern.split('/').filter(Boolean)
  const a = pathname.split('/').filter(Boolean)
  if (p.length !== a.length) return false
  return p.every((seg, i) => seg.startsWith(':') || seg === a[i])
}

/**
 * The sprint that delivers `pathname`.
 *
 * <p>An unlisted path is sprint 1 — visible. A screen nobody remembered to
 * classify should show up and be noticed, not vanish silently.
 */
export function sprintOf(pathname: string): Sprint {
  let best: SprintRoute | null = null
  for (const route of ROUTES) {
    if (!matches(route.path, pathname)) continue
    if (!best || specificity(route.path) > specificity(best.path)) best = route
  }
  return best ? best.sprint : 1
}

/** Whether this build shows `pathname`. */
export function isUnlocked(pathname: string): boolean {
  return sprintOf(pathname) <= CURRENT_SPRINT
}

/** `true` when a locked entry should not be rendered at all. */
export function hideWhenLocked(): boolean {
  return LOCK_STYLE === 'hidden'
}

/** Whether anything at all is being held back — for a build banner. */
export function stagingActive(): boolean {
  return CURRENT_SPRINT < 4
}
